import axios from "axios";

import toast from "react-hot-toast";

import { endPoints } from "../../constant/api_constants/url";
import { ENDPOINT } from "../../constant/environment";
import { API_METHODS } from "../../enums";
import { iCountryData, iCountryDataResponse } from "../../interface/common";
import { AirlineApiResponse, HotelBrandApiResponse } from "../../types";
import { decryptData, encryptData, getHeaderConfig } from "../../utils/api/encryption";
import { fetchApi } from "../../utils/apiFunction";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { getSharedByUserId } from "../../hooks/useIsSharedCartView";
import { getRequestedItineraryIdField, rememberRequestedItineraryId } from "../../utils/booking/requestedItineraryId";
import { getUserTimezoneField } from "../../utils/userTimezone";
import {
  getBookingIdempotencyKeyField,
  rememberBookingIdempotencyKey,
} from "../../utils/booking/bookingIdempotencyKey";
import { iHotelRoomBookArgs } from "../hotelDetailsAPI";
import { handleCustomError } from "../../utils/handleCustomError";
import { captureException } from "@sentry/react";
import { isValidJWT } from "../../utils/authentication/isValidJWT";

export const getAirports = async (
  accessJWT: string,
  requestBody: any,
  controller?: AbortController,
  newAirportUrl?: boolean
) => {
  const url = newAirportUrl ? `${ENDPOINT}/app/v1/flight/airportsPredictive` : `${ENDPOINT}/app/v1/flight/airports`;
  const config = getHeaderConfig(controller?.signal, accessJWT);
  if (!accessJWT || !isValidJWT(accessJWT)) return null;
  try {
    consoleLog(["GET AIRPORTS PAYLOAD", requestBody]);
    const response = await axios.post(url, encryptData(requestBody), config);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["GET AIRPORTS RESPONSE", parseData]);
      if (parseData?.data) return parseData?.data;
      else return;
    } else return;
  } catch (error) {
    console.error("Error searching for airports:", error);
    throw error;
  }
};

export const getAirportsV2 = async (
  accessJWT: string,
  requestBody: any,
  controller?: AbortController,
  newAirportUrl?: boolean
) => {
  const url = newAirportUrl ? `${ENDPOINT}/app/v1/airportsPredictive` : `${ENDPOINT}/app/v1/airportSearch`;
  const body = {
    searchKey: requestBody,
  };
  const config = getHeaderConfig(controller?.signal, accessJWT);
  if (!accessJWT || !isValidJWT(accessJWT)) return null;
  try {
    consoleLog(["GET AIRPORTS PAYLOAD", requestBody]);
    const response = await axios.post(url, encryptData(body), config);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["GET AIRPORTS RESPONSE", parseData]);
      if (parseData?.data) return parseData?.data;
      else return;
    } else return;
  } catch (error) {
    console.error("Error searching for airports:", error);
    throw error;
  }
};

export const getFlightAvailability = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  fareId: string,
  userId: string
) => {
  const body = {
    version: "v2",
    id: `${encryptedKey}-${uniqueId}`,
    fareId: fareId,
    userId: userId,
  };
  return await fetchApi(accessJWT, endPoints.flightAvailability, API_METHODS.POST, body, null, controller);
};

export const getBaggageAndFareRulesForFlightSearch = async (
  accessJWT: string,
  requestBody: any,
  controller: AbortController
) => {
  const bodyWithVersion = {
    version: "v2",
    ...requestBody,
  };
  return await fetchApi(accessJWT, endPoints.fareRulesDetails, API_METHODS.POST, bodyWithVersion, null, controller);
};

export const getFlightDetails = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  fareId: string,
  userId: string
) => {
  const requestBody = {
    id: `${encryptedKey}-${uniqueId}`,
    fareId: fareId,
    userId: userId,
  };
  return await fetchApi(accessJWT, endPoints.flightDetails, API_METHODS.POST, requestBody, null, controller);
};

export const searchFlights = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  const bodyWithVersion = {
    version: "v3",
    ...requestBody,
  };
  return await fetchApi(accessJWT, endPoints.flightSearch, API_METHODS.POST, bodyWithVersion, null, controller);
};

export const searchHotels = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  const bodyWithVersion = {
    version: "v2",
    ...requestBody,
  };
  return await fetchApi(accessJWT, endPoints.hotelSearch, API_METHODS.POST, bodyWithVersion, null, controller);
};

export const suggestHotelNames = async (
  accessJWT: string,
  requestBody: { city: string; country?: string; query: string; limit?: number },
  controller?: AbortController
) => {
  return await fetchApi(accessJWT, endPoints.hotelNameSuggest, API_METHODS.POST, requestBody, null, controller);
};

export const getHotelLocationSuggestions = async (
  accessJWT: string,
  query: string,
  controller?: AbortController
) => {
  return await fetchApi(
    accessJWT,
    endPoints.hotelTypeaheadSuggestions,
    API_METHODS.POST,
    { q: query },
    null,
    controller
  );
};

export const getHotelDetails = async (
  accessJWT: string,
  hotelId: string,
  controller: AbortController,
  isLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const endpoint = endPoints.hotelDetail(hotelId);
  if (!accessJWT || !isValidJWT(accessJWT)) return null;
  try {
    if (isLoading) isLoading(true);
    const response = await fetchApi(accessJWT, endpoint, API_METHODS.GET, null, null, controller);
    if (isLoading) isLoading(false);
    return response;
  } catch (error) {
    if (isLoading) isLoading(false);
    return null;
  }
};

export interface iBookHotelAndFlightArgs {
  clientId: string;
  userId: string;
  paymentDetails: PaymentDetails;
  passengerDetails: PassengerDetail[];
  flight?: string;
  hotel?: iHotelRoomBookArgs[];
  flightFareId?: any;
  paymentId?: any;
  flightAvailabilityStatus?: string;
}

export interface PassengerDetail {
  roomInfo?: any;
  paxType: string;
  gender?: string;
  leadPax: boolean;
  title?: any;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  countryCode: string;
  mobileNumber: string;
  dateOfBirth?: string;
  infantData?: InfantData | null | undefined;
  document?: Document;
  operationalReference?: OperationalReference;
  frequentFlyerinfo?: FrequentFlyerinfo;
  meals?: string[] | null;
  countryName?: string;
  iso?: string;
}

export interface Document {
  type: string | null;
  number: string | null;
  nationality: string | null;
  issuedCountry: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
}

export interface FrequentFlyerinfo {
  flyerNumber: string;
  frequentFlyerProgram: string;
  airline: string;
}

export interface InfantData {
  title: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  document: Document;
}

export interface OperationalReference {
  specialAssistance: string | null;
}

export interface PaymentDetails {
  successUrl: string;
  cancelUrl: string;
  amount: string;
  currency: string;
  remarks: string;
}

export const bookHotelAndFlight = async (
  accessJWT: string,
  args: iBookHotelAndFlightArgs,
  isLoading?: any
): Promise<any> => {
  const URL = `${ENDPOINT}/app/v1/itinerary/prebooking`;
  const config = getHeaderConfig(null, accessJWT);
  if (!accessJWT || !isValidJWT(accessJWT)) return null;
  const sharedByUserId = getSharedByUserId();
  const body = {
    ...args,
    ...(sharedByUserId ? { shared_by_user_id: sharedByUserId } : {}),
    ...getRequestedItineraryIdField(),
    ...getUserTimezoneField(),
  };
  try {
    if (isLoading) isLoading(true);
    consoleLog([body, "payload for prebooking"]);
    const encryptedBody = encryptData(body);
    const response = await axios.post(URL, encryptedBody, config);
    if (isLoading) isLoading(false);
    toast.dismiss();

    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog([parseData, "prebooking response"]);
      if (parseData?.statusCode === 200 && parseData?.data) {
        rememberBookingIdempotencyKey(parseData.data?.idempotencyKey);
        rememberRequestedItineraryId(parseData.data?.itineraryID);
        return parseData?.data;
      }
      return { __error: true, ...parseData };
    } else return null;
  } catch (error: any) {
    consoleLog([error, "prebooking Error"]);
    if (isLoading) isLoading(false);
    const statusCode = error?.response?.status ?? error?.response?.data?.statusCode ?? 500;
    const errData = error?.response?.data ? (() => { try { return JSON.parse(decryptData(error.response.data)); } catch { return null; } })() : null;
    return { __error: true, statusCode, message: errData?.message || error?.message, rawError: errData?.rawError, details: errData?.rawError?.details };
  }
};

interface iConfirmBookHotelAndFlightArgs {
  itineraryId: string;
  userId: string;
  id: string;
  paymentId?: string;
  paymentIntentId?: string;
}

export const confirmBookHotelAndFlight = async (
  accessJWT: string,
  args: iConfirmBookHotelAndFlightArgs
) => {
  const URL = `${ENDPOINT}/app/v1/itinerary/booking`;
  const config = getHeaderConfig(null, accessJWT);
  const body = {
    ...args,
    ...getUserTimezoneField(),
    ...getBookingIdempotencyKeyField(),
  };
  try {
    consoleLog(["API REQUEST", URL, body]);
    if (!accessJWT || !isValidJWT(accessJWT)) return null;
    const response = await axios.post(URL, encryptData(body), config);

    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      if (parseData?.statusCode !== 200) {
        captureException({
          error: "BOOKING CONFIRMATION ERROR parseData?.statusCode !== 200",
          response: response,
        });
        return null;
      }
      if (parseData?.data) return parseData?.data;
      captureException({
        error: "BOOKING CONFIRMATION ERROR NULL",
        response: response,
      });
      return null;
    } else {
      captureException({
        error: "BOOKING CONFIRMATION ERROR NO RESPONSE DATA",
        response: response,
      });
      return null;
    }
  } catch (error: any) {
    toast.dismiss();
    captureException({
      error: "BOOKING CONFIRMATION ERROR Catch",
      response: error,
    });
    return null;
  }
};

export const fetchCountries = async (accessJWT: string): Promise<iCountryDataResponse> => {
  const response = await fetchApi(accessJWT, endPoints.county, API_METHODS.GET);
  if (Array.isArray(response?.data)) {
    response.data = response.data.filter(
      (country: iCountryData) => !/[a-zA-Z]/.test(country?.code ?? "")
    );
  }
  return response;
};

export const getHotelReviews = async (
  accessJWT: string,
  userId: string,
  hotelId: string,
  offset: number,
  count: number,
  controller?: AbortController
) => {
  consoleLog(["API REQUEST", endPoints.hotelReviews, { userId, hotelId, offset, count }]);
  return await fetchApi(
    accessJWT,
    endPoints.hotelReviews,
    API_METHODS.POST,
    {
      userId,
      hotelId,
      offset,
      count,
    },
    null,
    controller
  );
};

export const fetchFlightPreference = async (
  accessJWT: string,
  searchKey: string,
  userId: string
): Promise<AirlineApiResponse> => {
  const endpoint = endPoints.getAirlinePreference;
  return await fetchApi(accessJWT, endpoint, API_METHODS.POST, { searchQuery: searchKey, userId: userId });
};
export const fetchHotelBrands = async (
  accessJWT: string,
  searchKey: string,
  userId: string
): Promise<HotelBrandApiResponse> => {
  const endpoint = endPoints.getHotelBrands;
  return await fetchApi(accessJWT, endpoint, API_METHODS.POST, { searchQuery: searchKey, userId: userId });
};

export const fetchFairDetails = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  userId: string
) => {
  const isChatFlightOrigin = sessionStorage.getItem("_zenvoya_chat_flight_origin") === "1";
  const requestBody = {
    version: isChatFlightOrigin ? "v3" : "v2",
    id: `${encryptedKey}-${uniqueId}`,
    userId: userId,
  };

  return await fetchApi(accessJWT, endPoints.fareDetails, API_METHODS.POST, requestBody, null, controller);
};

export const applyPromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.promoCode, API_METHODS.POST, requestBody, null, controller);
};

export const removePromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.removePromoCode, API_METHODS.POST, requestBody, null, controller);
};

export const changePromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.changePromoCode, API_METHODS.POST, requestBody, null, controller);
};
